import React from "react";
import htmlParse from "html-react-parser";
import Button from "../../../../Foundation/Button/Button";
import { _LandingHighlighProductMargauxMotinRoot } from "./LandingHighlighProductMargauxMotin.styled";
import Typography from "../../../../Foundation/Typography/Typography";
import LandingSectionBase, {
  ILandingSectionBaseProps,
} from "../../LandingSectionBase/LandingSectionBase";
import FlexBox from "../../../FlexBox/FlexBox";
import CompositionItem from "../CompositionItem/CompositionItem";
import { ICompositionVariant } from "../LandingHighlighProduct";
import { ImageFit } from "../../../Image/interface/ImageData";
import ImageResponsive from "../../../Image/ImageResponsive";
import MargauxMotinPriceList from "./MargauxMotinPriceList/MargauxMotinPriceList";
import DecorationBox from "../../../DecorationBox/DecorationBox";

interface ILandingHighlighProductMargauxMotinProps {
  image?: string;
  video?: string;
  animation?: ILandingSectionBaseProps["animation"];
  title: string;
  subtitle?: string;
  description?: string;
  ctaLabel: string;
  onClick?: () => void;
  backgroundImage?: {
    desktop?: string;
    mobile?: string;
  };
  imageLeft?: boolean;
  compositions?: ICompositionVariant;
  compositionLabel: string;
  price?: string;
  priceSubtitle?: string;
  product_display_from_mention?: string;
  product_price_vae?: string;
  product_price_vad?: string;
  vaeLabel?: string;
  vadLabel?: string;
  variant?: "top-center-title" | "composition-title";
  hideCta?: boolean;
}

const LandingHighlighProductMargauxMotin = (
  props: ILandingHighlighProductMargauxMotinProps
) => {
  const {
    image,
    title,
    subtitle,
    description,
    ctaLabel = "Je découvre",
    onClick,
    backgroundImage,
    imageLeft = false,
    compositionLabel,
    compositions,
    price,
    priceSubtitle,
    product_display_from_mention,
    product_price_vae,
    product_price_vad,
    variant = "composition-title",
    vaeLabel,
    vadLabel,
    hideCta = false,
  } = props;
  const isTopTitle = variant === "top-center-title";
  const topContent = (
    <FlexBox
      direction="column"
      gap={12}
      className="top-container"
      style={{ maxWidth: 421 }}
    >
      <Typography variant="h6" style={{ maxWidth: 347 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="margaux2" color="margauxPink">
          {htmlParse(subtitle + "")}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="greyText2">
          {htmlParse(description + "")}
        </Typography>
      )}
    </FlexBox>
  );

  const desktopAspectRatio = 711 / 503;
  const mobileAspectRatio = 343.0 / 242.67;
  return (
    <_LandingHighlighProductMargauxMotinRoot>
      <LandingSectionBase
        className="landing-section-base-margaux-motin"
        boxContainer={true}
        topVariant="align-left"
        topPosition={(isTopTitle && "top") || undefined}
        topContentSize={"default"}
        backgroundImage={backgroundImage}
        imageAspectRatio={desktopAspectRatio}
        renderImage={() => {
          return (
            <>
              <span className="">
                <ImageResponsive
                  {...{
                    alt: "",
                    desktop: {
                      image: {
                        id: image,
                        fit: ImageFit.COVER,
                        quality: 80,
                        aspectRatio: desktopAspectRatio,
                        width: 904,
                      },
                      imgStyle: {
                        borderRadius: 4,
                        overflow: "hidden",
                        aspectRatio: desktopAspectRatio,
                        maxWidth: 904,
                      },
                    },
                    mobile: {
                      image: {
                        id: image,
                        fit: ImageFit.COVER,
                        quality: 100,
                        aspectRatio: mobileAspectRatio,
                        outputPixelDensities: [1, 2],
                        width: 711,
                      },
                      imgStyle: {
                        borderRadius: 4,
                        overflow: "hidden",
                        aspectRatio: mobileAspectRatio,
                        maxWidth: 711,
                      },
                    },
                  }}
                />
              </span>
              {/* decorations */}
              <DecorationBox
                className="decorations-1"
                imageName="uramaki"
                alt="uramaki"
                enableAnimation={true}
                animationType="slideRight"
                animationDelay={800}
                animationDistance={100}
                threshold={0.4}
              />
              <DecorationBox
                className="decorations-2"
                imageName="ebi"
                alt="ebi"
                enableAnimation={true}
                animationType="slideLeft"
                animationDelay={100}
                animationDistance={200}
                threshold={0.4}
              />
              <DecorationBox
                className="decorations-3"
                imageName="couple"
                alt="couple"
                enableAnimation={true}
                animationType="slideLeft" 
                animationDistance={400}
                threshold={0.4}
              />
            </>
          );
        }}
        imageLeft={imageLeft}
        topContent={topContent}
        onImageClick={onClick}
        bottomContent={
          <FlexBox direction="column" gap={26}>
            {/* price */}
            <div className="price-list-container">
              <MargauxMotinPriceList
                price={price}
                product_price_vae={product_price_vae}
                product_price_vad={product_price_vad}
                product_display_from_mention={product_display_from_mention}
                priceSubtitle={priceSubtitle}
                vaeLabel={vaeLabel}
                vadLabel={vadLabel}
              />
            </div>

            <FlexBox
              mt={32}
              direction="column"
              gap={17}
              className="composition-container"
            >
              <Typography variant="ui">{compositionLabel}</Typography>
              {!compositions ? null : compositions.type === "box" ? (
                <FlexBox
                  direction="column"
                  gap={2}
                  className="composition-items"
                >
                  {compositions.list.map((item, index) => {
                    const nextItem = compositions.list[index + 1];
                    return (
                      <CompositionItem
                        key={item.id_product}
                        product={item}
                        theme={"margaux"}
                        className={
                          nextItem && !nextItem.highlighted && item.highlighted
                            ? "composition-item-separator"
                            : undefined
                        }
                      />
                    );
                  })}
                </FlexBox>
              ) : compositions.type === "paragraph" ? (
                <Typography>{compositions.text}</Typography>
              ) : null}
            </FlexBox>

            {!hideCta && (
              <div>
                <Button className="landing-action-button" onClick={onClick}>
                  {ctaLabel}
                </Button>
              </div>
            )}
          </FlexBox>
        }
      />
    </_LandingHighlighProductMargauxMotinRoot>
  );
};

export default LandingHighlighProductMargauxMotin;
